import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxSearchInput,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import { useDebounce } from '@/hooks/useDebounce';
import { requisitionInstitutesQueryOptions } from '../api/marketPromotionP2.api';
import type { RequisitionInstitute } from '../types/marketPromotionP2.types';

interface RequisitionInstituteSelectProps {
  id: string;
  thanaId: number | undefined;
  value: number | undefined;
  onChange: (instituteId: number | undefined) => void;
}

interface InstituteOption {
  label: string;
  value: number;
  code: string;
  where: string;
}

export function RequisitionInstituteSelect({
  id,
  thanaId,
  value,
  onChange,
}: RequisitionInstituteSelectProps) {
  const [query, setQuery] = useState('');
  const search = useDebounce(query, 300);

  const { data, isLoading, isFetching } = useQuery(
    requisitionInstitutesQueryOptions(thanaId, search),
  );

  const items: InstituteOption[] = (data ?? []).map((institute: RequisitionInstitute) => ({
    label: institute.instituteName,
    value: institute.instituteId,
    code: institute.instituteCode === null ? '' : String(institute.instituteCode),
    where: [institute.thanaName, institute.districtName].filter(Boolean).join(', '),
  }));

  const selected = items.some((item) => item.value === value) ? (value ?? null) : null;

  return (
    <Combobox<number>
      items={items}
      value={selected}
      onValueChange={(next) => onChange(next ?? undefined)}
      filter={() => true}
    >
      <ComboboxTrigger id={id}>
        <ComboboxValue
          placeholder={isLoading ? LOADING_PLACEHOLDER : 'Search by name, EIIN or code'}
        />
        <ComboboxIcon />
      </ComboboxTrigger>

      <ComboboxContent>
        <ComboboxSearchInput
          placeholder="Type a name, EIIN or institute code"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <ComboboxList>
          {(item: InstituteOption) => (
            <ComboboxItem key={item.value} value={item.value}>
              <span className="flex flex-col">
                <span>{item.label}</span>
                <span className="text-xs text-muted-foreground">
                  {[item.code, item.where].filter(Boolean).join(' · ')}
                </span>
              </span>
            </ComboboxItem>
          )}
        </ComboboxList>
        <ComboboxEmpty>
          {isFetching
            ? 'Searching…'
            : 'No school you cover matches that. Only the institutes mapped to you are listed.'}
        </ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}
